import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mvpApi } from '../../services/api';

/**
 * Reddy-style assigned advisor control: name + chevron opens list of admins; PATCHes case assignment.
 */
export default function CaseAdvisorPicker({
  userId,
  sessionId,
  assignedAdvisorId,
  assignedAdvisorName,
  onAfterSuccess,
  onNotify,
}) {
  const [advisorsList, setAdvisorsList] = useState([]);
  const [showAdvisorPopover, setShowAdvisorPopover] = useState(false);
  const [assigningAdvisor, setAssigningAdvisor] = useState(false);
  const advisorPopoverRef = useRef(null);

  const displayName = assignedAdvisorName || '';
  const currentId = assignedAdvisorId != null ? Number(assignedAdvisorId) : null;

  useEffect(() => {
    let mounted = true;
    mvpApi
      .getAdminAdvisors()
      .then((res) => {
        if (!mounted) return;
        const list = res?.data?.advisors || [];
        const sorted = [...list].sort((a, b) =>
          String(a.name || a.email || '').localeCompare(String(b.name || b.email || '')),
        );
        setAdvisorsList(sorted);
      })
      .catch(() => {
        if (mounted) setAdvisorsList([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!showAdvisorPopover) return;
    const onDoc = (e) => {
      if (advisorPopoverRef.current && !advisorPopoverRef.current.contains(e.target)) {
        setShowAdvisorPopover(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showAdvisorPopover]);

  const onSelectAdvisor = useCallback(
    async (advisorUserId) => {
      setAssigningAdvisor(true);
      try {
        await mvpApi.patchCaseAssignment(userId, sessionId, {
          advisor_user_id: advisorUserId,
        });
        if (onAfterSuccess) await onAfterSuccess();
        setShowAdvisorPopover(false);
        if (onNotify) {
          if (advisorUserId != null) {
            const picked = advisorsList.find((a) => Number(a.id) === Number(advisorUserId));
            const label = picked?.name || picked?.email || 'Advisor';
            onNotify(`Advisor changed to ${label}`);
          } else {
            onNotify('Advisor removed');
          }
        }
      } catch (err) {
        const raw = err?.response?.data?.detail;
        const msg =
          typeof raw === 'string'
            ? raw
            : Array.isArray(raw)
              ? raw.map((x) => x?.msg || x).join(', ')
              : err?.message || 'Could not update advisor';
        if (onNotify) onNotify(typeof msg === 'string' ? msg : 'Could not update advisor');
      } finally {
        setAssigningAdvisor(false);
      }
    },
    [userId, sessionId, onAfterSuccess, onNotify, advisorsList],
  );

  return (
    <div className="relative" ref={advisorPopoverRef}>
      <button
        type="button"
        disabled={assigningAdvisor}
        onClick={() => setShowAdvisorPopover((v) => !v)}
        className={`text-sm font-medium transition-colors group flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed ${
          displayName
            ? 'text-slate-800 hover:text-emerald-700 hover:underline cursor-pointer'
            : 'text-slate-400 italic hover:text-emerald-600 hover:no-underline cursor-pointer'
        }`}
      >
        {assigningAdvisor ? 'Saving…' : displayName || 'Unassigned'}
        <i className="ri-arrow-down-s-line text-xs text-slate-400 group-hover:text-emerald-600 transition-colors" />
      </button>
      {showAdvisorPopover ? (
        <div
          className="absolute top-full left-0 mt-1 z-30 bg-white border border-slate-200 rounded-xl overflow-hidden min-w-[170px] max-h-[min(320px,70vh)] overflow-y-auto"
          style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}
        >
          <button
            type="button"
            onClick={() => onSelectAdvisor(null)}
            className={`w-full text-left px-3.5 py-2 text-xs transition-colors cursor-pointer whitespace-nowrap ${
              currentId == null
                ? 'bg-emerald-50 text-emerald-700 font-semibold'
                : 'text-slate-400 italic hover:bg-slate-50'
            }`}
          >
            Unassigned
            {currentId == null ? <i className="ri-check-line ml-2 text-emerald-600" /> : null}
          </button>
          {advisorsList.map((a) => {
            const id = Number(a.id);
            const label = a.name || a.email || `Advisor #${a.id}`;
            const selected = currentId != null && !Number.isNaN(id) && currentId === id;
            return (
              <button
                type="button"
                key={String(a.id)}
                onClick={() => onSelectAdvisor(id)}
                className={`w-full text-left px-3.5 py-2 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  selected
                    ? 'bg-emerald-50 text-emerald-700 font-semibold'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {label}
                {selected ? <i className="ri-check-line ml-2 text-emerald-600" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
